<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 call-container theme-color-wrapper p-1">
        <div id="videoContainer">
          <audio id="mt-audio" class="rounded centered" autoplay playsinline />
          <div class="row video-container-row">
            <!-- Prisoner Media -->
            <div v-if="!loadingVisit" class="col-12">
              <div id="remote-media">
                <div class="prisoner-pause-btn">
                  <i v-if="!prisonerMuted" class="fas fa-microphone" />
                  <i v-if="prisonerMuted" class="fas fa-microphone-slash" />
                </div>
                <video id="remote-video-feed" class="rounded centered" style="position: absolute" width="100%"
                       height="100%"
                       autoplay playsinline :muted="muteRemoteAudio" :class="{ 'soft-pause': muteRemoteVideo }"
                />
                <!-- Visitor Media -->
                <div class="visitor-media">
                  <div id="local-media">
                    <div class="prisoner-pause-btn hover-pointer cursor-pointer">
                      <i v-if="!muted" class="fas fa-microphone" @click="btnSetMuted(true)" />
                      <i v-if="muted" class="fas fa-microphone-slash" @click="btnSetMuted(false)" />
                    </div>
                    <video id="local-video-feed" class="rounded centered" width="100%" height="100%" autoplay
                           playsinline muted="muted"
                    />
                  </div>
                </div>

                <p v-if="waitingForPrisoner && callState === 'ACTIVE'" class="warning-text">
                  {{ getWaitingForText() }}
                </p>
                <p v-if="prisonerNoVideo && callState === 'ACTIVE'" class="warning-text">
                  {{ getPrisonerNoVideoText() }}
                </p>

                <div v-if="callState === 'PAUSED_INVALID_FACES' || callState === 'PAUSED_ADULT_CONTENT'"
                     class="warning-text__behaviour-bg"
                />
                <div v-if="callState === 'PAUSED_INVALID_FACES'" class="warning-text warning-text__behaviour">

                  <span class="warning-text__behaviour__icon"><i class="fas fa-exclamation-circle" /></span>
                  <h2 class="warning-text__behaviour__title">Someone unexpected on the call</h2>
                  <p>Only approved people should be on the call</p>
                  <p>
                    You need to make sure your whole face is on camera, and there's a plain background behind you
                  </p>
                </div>

                <div v-if="callState === 'PAUSED_ADULT_CONTENT'" class="warning-text warning-text__behaviour">
                  <span class="warning-text__behaviour__icon"><i class="fas fa-exclamation-circle" /></span>
                  <h2 class="warning-text__behaviour__title">Possible inappropriate behaviour</h2>
                  <p>All callers must be fully dressed and follow rules for social calls</p>
                  <p>Video will be stopped until this is fixed</p>

                  <h5 style="margin-top: 15px">Did we get it wrong?</h5>
                  <p>Sometimes this happens when there's a baby on camera or lots of skin showing</p>

                </div>
                <p v-else-if="callState === 'PAUSED_MT'" class="warning-text">
                  {{ getMtPausedText() }}
                </p>
                <p v-else-if="callState === 'PAUSED_APP_IN_BACKGROUND'" class="warning-text">
                  {{ getPausedAppInBackgroundText() }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <section class="col-12 mt-2 p-1 theme-color-wrapper call-action-bar">
        <div class="row align-items-center call-action-bar__row">
          <div :class="$mq === 'sm' ? 'col-12 mb-1' : 'col-4'" class="text-center d-flex justify-content-center">
            <clock />
          </div>
          <div :class="$mq === 'sm' ? 'col-12 mb-1' : 'col-4'" class="d-flex">
            <b-btn class="mx-auto px-4" variant="danger" :disabled="callState === 'PAUSED_MT'" @click="endCall()">
              <span class="fas fa-phone-slash" /> End Call
            </b-btn>
          </div>
          <div v-if="!loadingVisit" :class="$mq === 'sm' ? 'col-12 mb-1' : 'col-4'"
               class="text-center d-flex justify-content-center"
          >
            <h4 class="mb-0">Call Ends: {{ getVisitEndTime() }}</h4>
          </div>
        </div>
        <div v-if="!loadingVisit && !loadingPrison" class="row d-flex justify-content-center call-action-bar__row">
          <div :class="$mq === 'sm' ? 'col-12 mb-1' : 'col-6'" class="text-center">
            <h4 v-if="visit.should_record" class="mb-0">Recording <span class="fas fa-circle text-danger" /></h4>
          </div>
          <div v-if="mounted" :class="$mq === 'sm' ? 'col-12' : 'col-6'" class="text-right">
            <visit-countdown
              :visit="visit"
              :prison="prison"
              :leave-call="endCallDueToCountdown"
            />
          </div>
        </div>
      </section>
    </div>
    <call-features-panel v-if="visit && visit.allow_chat_features"></call-features-panel>
  </div>
</template>

<style type="text/css" scoped lang="scss">
body {
  background: black;
}

.visitor-media {
  width: 200px;
  position: absolute;
  top: 15px;
  left: 15px;

  @include mobile() {
    width: 150px;
  }
}

.call-container {
  height: 87vh;
  max-height: 87vh;
  margin-top: 1em;
  overflow: hidden;
}

#local-media {
  position: relative;
}

.prisoner-pause-btn {
  color: white;
  position: absolute;
  top: 5px;
  right: 10px;
  z-index: 1;
}

#videoContainer {
  height: 100%;
}

.video-container-row {
  height: 100%;
}

#remote-media {
  position: relative;
  background: black;
  height: 100%;
}

.warning-text {
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  text-align: center;
  transform: translate(-50%, -50%);
  font-weight: bold;
}

.warning-text__behaviour-bg {
  content: "";
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.2);
}

.warning-text__behaviour {
  width: 80%;
}

.warning-text__behaviour__title {
  margin-bottom: 30px;
}

.warning-text__behaviour p {
  font-weight: 100;
  max-width: 70%;
  margin: auto;
  margin-bottom: 10px;
}

.warning-text__behaviour__icon {
  color: #E63365;
  font-size: 40pt;
  position: relative;
}

.warning-text__behaviour__icon ::after {
  content: "";
  position: absolute;
  top: 18px;
  left: 10px;
  background-color: white;
  width: 25px;
  height: 35px;
  z-index: -1;
}

.soft-pause {
  filter: blur(50px) brightness(0.6);
}

.call-action-bar {
  position: fixed;
  bottom: 0;
  width: calc(100vw - (100vw - 100%) - calc(2rem * 2));
  right: 0;
  margin: 0 2rem 0;
  &__row {
    @include mobile-portrait() {
      flex-direction: column;
    }
  }
}

.menu-expanded {
  .call-action-bar {
    width: calc(100vw - (100vw - 100%) - calc(2rem * 2) - 260px) !important;
  }
}
</style>

<script>
import Vvrtc from 'vvrtc-js';
import axios from '@axios';
import {
  CALL_EVENT_ACTION_SUB_TYPES,
  CALL_EVENT_EVENT_SUB_TYPES,
  CALL_EVENT_TYPES,
} from '@/util/callActionsAndEvents';
import moment from 'moment';
import { getDateTimeOfSlot } from '@/util/helpers';
import io from 'socket.io-client';
import { logger } from '@/util/logger';
import CallEventsService from '@/services/CallEventsService';
import Clock from '../clock/Clock.vue';
import { REST_ENDPOINT, WS_ENDPOINT } from '../../../endpoints';
import VisitCountdown from '../clock/VisitCountdown.vue';
import CallFeaturesPanel from '@/components/callFeaturesPanel/CallFeaturesPanel';
import { mapGetters } from 'vuex';

let socket;

const CALL_STATES = {
  ACTIVE: 'ACTIVE',
  PAUSED_INVALID_FACES: 'PAUSED_INVALID_FACES',
  PAUSED_MT: 'PAUSED_MT',
  PAUSED_ADULT_CONTENT: 'PAUSED_ADULT_CONTENT',
  PAUSED_APP_IN_BACKGROUND: 'PAUSED_APP_IN_BACKGROUND',
};

Vvrtc.logAll = false;

export default {
  name: 'VisitCallVvrtc',
  components: {
    VisitCountdown,
    Clock,
    CallFeaturesPanel,
  },
  data() {
    return {
      // Snapshot
      snapshotIntervalId: null,
      // General
      loadingVisit: true,
      loadingPrison: true,
      waitingForPrisoner: true,
      // Call Control
      prisonerMuted: false,
      prisonerNoVideo: false,
      muteRemoteAudio: false,
      muteRemoteVideo: false,
      muted: false,
      // Util
      wsConnected: false,
      callStateLastUpdate: null,
      latestCallStateIntervalId: null,
      screenShotLimit: 2,
      screenShotsTaken: 0,
      leaving: false,
      leavingNaturally: false,
      mounted: false,
      // State
      callState: CALL_STATES.ACTIVE,
      // Data
      visitId: this.$route.params.id,
      visit: {},
      prison: null,
      prisonerStream: null,
      localStream: null,
      leavingDueToCallCountdown: false,
    };
  },
  computed: {
    ...mapGetters('auth', { isIntakeUser: 'isIntakeUser' }),
  },
  async mounted() {
    // Fetch info
    this.fetchVisit();

    // Wait for these two before rendering
    await this.joinRoom();
    await this.handleWS();

    // Check for screen shots
    this.checkForScreenshots();

    // Setting a flag as it's important not to let anything move away from this page before it is mounted. I've added
    // v-if="mounted" on the countdown timer to make sure it only kicks in once the component is actually mounted
    // otherwise the beforeDestroy and Destroyed methods are called, first, then the mounted conde continues afterwards.
    // Any component that may redirect away from this page needs to ensure the module is mounted first.
    this.mounted = true;
  },
  async beforeDestroy() {
    if (this.localStream != null) {
      this.localStream.getTracks().forEach(track => {
        track.stop();
      });
    }

    this.$store.dispatch('callFeatures/resetCallFeatures');

    await this.leaveRoomIfJoined();
  },
  methods: {
    // Data
    fetchPrisonInfo(prisonId) {
      axios({
        url: `${REST_ENDPOINT}/api/v1/prisons/single-prison/${prisonId}`,
        method: 'GET',
      }).then(res => {
        this.prison = res.data;
        this.screenShotLimit = this.prison.screen_shot_limit;
        this.loadingPrison = false;
      }).catch(err => {
        logger.error('Unable to get prison ', err, null, 'VisitCall:fetchPrisonInfo');
        this.$toast.warning('There was a problem getting a your establishment details, please refresh and try again.', {
          toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
        });
      });
    },
    fetchVisit() {
      axios({
        url: `${REST_ENDPOINT}/api/v1/visits/single-visit/${this.visitId}`,
        method: 'GET',
      }).then(res => {
        this.visit = res.data;
        this.$store.dispatch('callFeatures/setUpVisit', this.visit);

        if (this.isCallReady(this.visit)) {
          this.loadingVisit = false;
          this.fetchPrisonInfo(this.visit.prison.prison_id);
        } else {
          this.goToWaitingRoom();
        }
      }).catch(err => {
        logger.error('There was a problem getting your video call details, please refresh and try again.', err, null, 'VisitCall:fetchVisit');
        this.$toast.warning('There was a problem getting your video call details, please refresh and try again.', {
          toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
        });
        // Redirect
        this.navigateAway();
      });
    },
    isCallReady(call) {
      return call.call_state.call_security_joined && call.call_state.prisoner_in_call;
    },
    pollLatestCallAction() {
      this.latestCallStateIntervalId = setInterval(() => {
        logger.message('>> Polling');
        this.fetchLatestCallAction();
      }, 5000);
    },
    fetchLatestCallAction() {
      axios({
        url: `${REST_ENDPOINT}/api/v1/call-events/for-visit/${this.visitId}/latest-action`,
        method: 'GET',
      }).then(res => {
        const latestCallAction = res.data;

        if (latestCallAction) {
          let isAfter = true;
          // Check we have a last call state updated
          if (this.callStateLastUpdate) {
            const fetchedOccurredOn = moment(latestCallAction.occurred_on).toDate();
            const localOccurredOn = moment(this.callStateLastUpdate).toDate();
            isAfter = moment(fetchedOccurredOn).isAfter(localOccurredOn, 'second');
          }

          // Check if latest call action is newer than callStateLastUpdate
          if (isAfter) {
            this.handleCallEvent(latestCallAction);
          }
        }
      }).catch(err => {
        logger.error('Cannot get latest call action', err, { visitId: this.visitId }, 'VisitCall:fetchLatestCallAction');
      });
    },
    // Websockets
    async handleWS() {
      const userToken = localStorage.getItem('userTokenOffsite');
      socket = io.connect(WS_ENDPOINT, {
        query: {
          token: `Bearer ${userToken}`,
        },
        transports: ['websocket'],
      });

      socket.on('connect', () => {
        this.joinVisitorRoomWS();
      });

      socket.on('connect_error', err => {
        logger.warn('WS Connection error', err, {
          visitId: this.visitId,
          wsWasConnected: this.wsConnected,
        }, 'VisitCall:handleWS:connect_error');
        if (this.wsConnected) {
          // Start polling
          this.pollLatestCallAction();
          // Stop snapshotting
          clearInterval(this.snapshotIntervalId);
          // Set WS Connected to false
          this.wsConnected = false;
        }
      });

      socket.on('reconnect', () => {
        logger.message('WS reconnected');
        // Clear polling interval
        if (this.latestCallStateIntervalId) {
          clearInterval(this.latestCallStateIntervalId);
          this.latestCallStateIntervalId = null;
        }
        if (!this.wsConnected) {
          this.wsConnected = true;
        }
      });

      // Handle call events from the server
      socket.on('callEventFromServer', this.handleCallEvent);
    },
    joinVisitorRoomWS() {
      socket.emit('offsiteVisitorJoinRoom', this.visitId, err => {
        if (err) {
          logger.error('Error joining visitor WS Room', err, { visitId: this.visitId }, 'VisitCall:joinVisitorRoomWS');
          // Leave the room if joined
          this.leaveRoomIfJoined();
          // Alert user
          this.$toast.warning('Error connecting to server. Please check you are connected to the internet and try again.', {
            toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
          });
          // Redirect
          this.navigateAway();
        } else {
          console.log('>> CONNECTED as VISITOR');
          this.wsConnected = true;
          // Stop polling
          clearInterval(this.latestCallStateIntervalId);
          this.latestCallStateIntervalId = null;
          // Clear snapshot if there is one
          if (this.snapshotIntervalId) {
            clearInterval(this.snapshotIntervalId);
          }
          logger.message('>> Connected to WS as Visitor');

          const prisonSnapshotEnabled = this.prison && this.prison.send_preview_images_from_pt;

          // Begin snapshots if on cs_version is -1 or prison requires snapshots from client
          if ((typeof (this.visit.cs_version) !== 'undefined' && this.visit.cs_version === -1) || prisonSnapshotEnabled) {
            this.snapshotCall();
          }
        }
      });
    },
    snapshotCall() {
      const localVideoElement = document.getElementById('local-video-feed');

      const wsSnapshots = {
        visit_id: this.visitId,
        image: null,
        user_id: this.prisonerNumber,
        user_type: 'visitor',
      };

      if (localVideoElement) {
        wsSnapshots.image = this.getImageDataUrlFromVideo(localVideoElement);
      }

      socket.emit('callPreviewImageData', wsSnapshots);

      // Call every 1500 seconds
      this.snapshotIntervalId = setTimeout(() => {
        this.snapshotCall();
      }, 1500);
    },
    getImageDataUrlFromVideo(videoElement) {
      return this.getImageDataUrl(videoElement, 0.1);
    },
    getImageDataUrl(videoElement, quality) {
      if (!videoElement) {
        return null;
      }
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      canvas.getContext('2d').drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', quality);
    },
    handleCallEvent(callEvent) {
      switch (callEvent.sub_type) {
        case CALL_EVENT_ACTION_SUB_TYPES.TERMINATED:
          // eslint-disable-next-line no-case-declarations
          let text = 'Your call has been terminated by site security.';

          if (this.visit.termination_reason) {
            text += `\nThe reasons for termination are:\n
            'Please make sure to abide by all rules during your visit.'`
          }

          // Warn user
          this.$swal({
            title: 'Call Terminated',
            text,
            icon: 'warning',
          });
          // Leave the call gracefully
          this.callTerminated();
          break;
        case CALL_EVENT_ACTION_SUB_TYPES.PAUSED_MT:
          // Warn user
          this.$toast.warning(`The call has been paused by a establishment officer.
           ${this.visit.prisoner.firstname} can no longer see or hear you.`, {
            toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
          });
          this.callPausedMT();
          break;
        case CALL_EVENT_ACTION_SUB_TYPES.PAUSED_ADULT_CONTENT:
        case CALL_EVENT_ACTION_SUB_TYPES.SOFT_PAUSE_ADULT_CONTENT:
          // Warn user
          this.$toast.warning('Call paused due to adult content detected in stream', {
            toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
          });
          this.callPausedAdultContent();
          break;
        case CALL_EVENT_ACTION_SUB_TYPES.RESUMED_MT:
          // Alert user
          this.$toast.success('The call has been resumed', {
            toastClassName: ['toast-std', 'success-toast'], timeout: 5000,
          });
          this.resumeCall();
          break;
        case CALL_EVENT_ACTION_SUB_TYPES.RESUMED_ADULT_CONTENT:
        case CALL_EVENT_ACTION_SUB_TYPES.SOFT_PAUSE_RESUMED:
          // Check we are not in an MT Pause as this trumps all
          if (this.callState !== CALL_STATES.PAUSED_MT) {
            // Alert user
            this.$toast.success('The call has been resumed', {
              toastClassName: ['toast-std', 'success-toast'], timeout: 5000,
            });
            this.resumeCall()
          }
          break;
        case CALL_EVENT_EVENT_SUB_TYPES.VISITOR_SCREEN_SHOT:
          // Increase screen shots taken
          this.screenShotsTaken += 1;
          if (this.screenShotsTaken < this.screenShotLimit) {
            // Warn user
            this.$swal({
              title: 'WARNING: Screen Shot Detected',
              text: 'Please be aware that if you take another screen shot your call will be terminated.',
              icon: 'warning',
            });
          }
          break;
        case CALL_EVENT_EVENT_SUB_TYPES.PRISONER_MUTED:
          // Alert user
          this.$toast.warning('Your contact has muted their mic so you can not hear them.', {
            toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
          });
          this.setPrisonerMuted(true);
          break;
        case CALL_EVENT_EVENT_SUB_TYPES.PRISONER_UNMUTED:
          // Alert user
          this.$toast.warning('Your contact has unmuted their mic so you can hear them.', {
            toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
          });
          this.setPrisonerMuted(false);
          break;
        case CALL_EVENT_EVENT_SUB_TYPES.PRISONER_LEFT:
          // Alert user
          this.$swal({
            title: 'Call Ended',
            text: `Call ended by ${this.visit.prisoner.firstname}. If you think this was an error you can rejoin the call.`,
            icon: 'info',
            button: 'Ok',
          });
          // Leave the call
          this.leaveRoomIfJoined();
          break;
        case CALL_EVENT_EVENT_SUB_TYPES.NEW_CHAT_MESSAGE:
          if (callEvent.action_by !== 'Visitor') { // only add messages from prisoner to chat
            this.$store.dispatch('callFeatures/addParticipantMessage', callEvent.meta);
          }

          break;
        case CALL_EVENT_EVENT_SUB_TYPES.HAND_RAISED:
          this.$store.commit('callFeatures/SET_HAND_RAISED', callEvent.meta);
          break;
        default:
          logger.info(`Unknown call event sub type: ${callEvent}`, null, {
            visitId: this.visitId,
            event: callEvent,
          }, 'VisitCall:handleCallEvent');
          break;
      }
    },
    // Pause Controls
    callPausedMT() {
      if (this.callState !== CALL_STATES.PAUSED_MT) {
        // Set the call state
        this.callState = CALL_STATES.PAUSED_MT;
        this.callStateLastUpdate = new Date();
        // Pause Call
        this.pauseCall();
      }
    },
    callPausedAdultContent() {
      // Check we are not in MT Pause. MT pause trumps all
      if (this.callState === CALL_STATES.PAUSED_MT) {
        return;
      }
      if (this.callState !== CALL_STATES.PAUSED_ADULT_CONTENT) {
        // Set the call state
        this.callState = CALL_STATES.PAUSED_ADULT_CONTENT;
        this.callStateLastUpdate = new Date();
        // Pause Call
        this.pauseCall();
      }
    },
    pauseCall() {
      // Mute and Hide prisoners audio and video
      this.mutePrisonerAndHideVideo();
      // Mute our own tracks
      this.removeAndUnPublishLocalTracks();
    },
    async resumeCall() {
      // Update call state
      this.callState = CALL_STATES.ACTIVE;
      this.callStateLastUpdate = new Date();
      // Remove any mutes on remote media
      this.muteRemoteAudio = false;
      this.muteRemoteVideo = false;

      console.log('>> resumeCall');
      // Remove any pre-existing prisoner video
      const remoteVideoFeed = document.getElementById('remote-video-feed');
      if (remoteVideoFeed) {
        // Remove any existing tracks
        await Vvrtc.removeMediaStream(remoteVideoFeed);
        // Remove hidden class
        remoteVideoFeed.classList.remove('hidden');
      }
      // Reattach prisoner video
      Vvrtc.attachMediaStream(remoteVideoFeed, this.prisonerStream);
      // Unmute prisoner
      this.setPrisonerMuted(false);
      // Republish local tracks
      this.createAndRepublishLocalTracks();
      // Set muted to false
      this.setMuted(false);
    },
    // Call Controls
    mutePrisonerAndHideVideo() {
      // Remove video
      const remoteVideoFeed = document.getElementById('remote-video-feed');
      if (remoteVideoFeed) {
        remoteVideoFeed.classList.add('hidden');
        Vvrtc.removeMediaStream(remoteVideoFeed);
      }
      // Mute Prisoner Audio
      this.setPrisonerMuted(true);
    },
    removeAndUnPublishLocalTracks() {
      // Remove own feeds
      Vvrtc.unpublishOwnFeed();
      // Mute
      this.setMuted(true);
    },
    createAndRepublishLocalTracks() {
      // Republish video
      Vvrtc.publishOwnFeed(true);
      // Unmute
      this.setMuted(false);
    },
    async joinRoom() {
      console.log('>> joinRoom');
      try {
        // Get room details from server
        const response = await axios({
          url: `${REST_ENDPOINT}/api/v1/call/${this.visitId}/tokens/`,
          method: 'GET',
        });

        // Set options
        const connectionOptions = {
          identity: response.data.identity,
          token: response.data.token,
          media: {
            audio: true,
            video: true,
          },
          servers: response.data.servers,
        };

        // Connect to the server
        await Vvrtc.connect(connectionOptions);

        // Handle any messages that come from the server
        this.handleMessages();

        // Join the room
        Vvrtc.joinRoom(this.visitId, response.data.roomPin);

        // Set visitor as joined
        this.setVisitorJoinedCalled();
      } catch (err) {
        logger.error(
          'Unable to join room', err, { visitId: this.visitId }, 'VisitCall:joinRoom',
        );

        this.$swal({
          title: 'Unable to join call',
          text: 'Please check your headset & webcam are connected and try again.',
          icon: 'warning',
          button: 'Ok',
        }).then(() => {
          // Redirect to visit details
          this.goToWaitingRoom();
        });
      }
    },
    /**
     * Handle Messages from Vvrtc
     */
    handleMessages() {
      // If there is an error connecting to the room
      Vvrtc.on('room:local:error', data => {
        const { error } = data;
        const { error_code } = data;

        let message = 'Unable to connect to room';

        // eslint-disable-next-line camelcase
        if (error_code === 426) {
          message = 'The room for your call does not exist';
        }

        this.connectionError(error, message);
      });

      // When the local Stream is attached, show the video
      Vvrtc.on(Vvrtc.EventsType.localStream, stream => {
        // Set the prisoner as 'joined'
        this.localStream = stream;
        const previewContainer = document.getElementById('local-video-feed');
        Vvrtc.attachMediaStream(previewContainer, stream, true);
      });

      // Error publishing own feed
      Vvrtc.on(Vvrtc.EventsType.localPublishError, data => {
        this.connectionError(data.error, 'There was an error publish your video Feed. Please try again.');
      });

      // If the sessions or room is destroyed
      Vvrtc.on(Vvrtc.EventsType.sessionDestroyed, this.sessionFinished);
      Vvrtc.on(Vvrtc.EventsType.roomDestroyed, this.sessionFinished);

      // when a remote stream is found
      Vvrtc.on(Vvrtc.EventsType.remoteStream, this.participantJoined);

      Vvrtc.on(Vvrtc.EventsType.remoteUnpublished, this.participantDisconnected);
      Vvrtc.on(Vvrtc.EventsType.remoteCleanup, this.participantDisconnected);

      // If there is a network issue, then the ice connection may be failed. Handle this here.
      Vvrtc.on(Vvrtc.EventsType.localIceState, state => {
        logger.info(`Ice State Changed: ${state}`);
        if (state === 'failed') {
          this.leaveRoomIfJoined();
          // Alert user
          this.$toast.warning('There was a problem with the internet connection. Please try reconnecting.', {
            toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
          });
          // Redirect
          this.navigateAway();
        }
      });
    },
    async callTerminated() {
      // Leave the room
      await this.leaveRoomIfJoined();
      // Redirect
      this.navigateAway();
    },
    endCallDueToCountdown() {
      this.leavingDueToCallCountdown = true;
      this.endCall();
    },
    async endCall() {
      // Leave the room
      await this.leaveRoomIfJoined();
      // Create event
      await this.createGeneralCallEvent({
        visit_id: this.visitId,
        type: CALL_EVENT_TYPES.EVENT,
        sub_type: CALL_EVENT_EVENT_SUB_TYPES.VISITOR_LEFT,
      });
    },
    async leaveRoomIfJoined() {
      console.log('>> leaveRoomIfJoined');
      if (this.leaving) {
        console.log('>> we\'re already leaving, so returning');
        return;
      }
      this.leavingNaturally = true;
      this.leaving = true;

      // Destroy socket
      if (socket) {
        socket.disconnect();
      }

      clearInterval(this.latestCallStateIntervalId);
      clearInterval(this.snapshotIntervalId);
      this.latestCallStateIntervalId = null;
      this.snapshotIntervalId = null;

      // await Vvrtc.unpublishOwnFeed();
      await Vvrtc.destroy();
    },
    btnSetMuted(isMuted) {
      // Check call state is active
      if (this.callState === CALL_STATES.ACTIVE) {
        this.setMuted(isMuted);
      }
    },
    setMuted(isMuted) {
      this.muted = isMuted;

      if (this.localStream) {
        const tracks = this.localStream.getAudioTracks();
        tracks[0].enabled = !isMuted;
      }

      this.createGeneralCallEvent({
        visit_id: this.visitId,
        type: CALL_EVENT_TYPES.EVENT,
        sub_type: isMuted ? CALL_EVENT_EVENT_SUB_TYPES.VISITOR_MUTED : CALL_EVENT_EVENT_SUB_TYPES.VISITOR_UNMUTED,
      });
    },
    setPrisonerMuted(isMuted) {
      this.prisonerMuted = isMuted;

      if (this.prisonerStream) {
        const tracks = this.prisonerStream.getAudioTracks();
        tracks[0].enabled = !isMuted;
      }
    },
    // Util
    participantJoined(data) {
      const identity = data.remoteFeed.identity.toLowerCase();
      logger.message(`>> Participant connected ${identity}`);

      if (this.leaving) {
        logger.message(`Ignoring participant ${identity} as we are leaving`);
        return;
      }

      if (identity.includes('prisoner')) {
        this.prisonerStream = data.stream;
        logger.message(`Adding new stream for Prisoner. Call state is currently:${this.callState}`);

        this.waitingForPrisoner = false;

        if (this.callState !== CALL_STATES.PAUSED_MT) {
          const previewContainer = document.getElementById('remote-video-feed');
          Vvrtc.attachMediaStream(previewContainer, data.stream, this.muteRemoteAudio);
          try {
            const videoTracks = data.stream.getVideoTracks();
            const remoteVideo = document.getElementById('remote-video-feed');
            if (videoTracks === null || videoTracks === undefined || videoTracks.length === 0) {
              if (remoteVideo) {
                // No remote video
                remoteVideo.classList.add('hidden');
              }
              this.prisonerNoVideo = true;
            } else {
              if (remoteVideo) {
                remoteVideo.classList.remove('hidden');
              }
              this.prisonerNoVideo = false;
            }
          } catch (err) {
            console.log('There was an error looking at the video tracks on the stream:', err);
          }
        }
      }
    },
    participantDisconnected(participant) {
      const identity = participant.identity.toLowerCase();
      console.log('Participant disconnected: ', identity);

      if (identity.includes('prisoner')) {
        console.log('Removing remote video feed.', identity);
        const remoteVideoFeed = document.getElementById('remote-video-feed');
        Vvrtc.removeMediaStream(remoteVideoFeed);
        // Remove the no video messages
        this.prisonerNoVideo = false;
      }
    },
    setVisitorJoinedCalled() {
      const visitorJoinedCallDto = {
        visit_id: this.visitId,
        type: CALL_EVENT_TYPES.EVENT,
        sub_type: CALL_EVENT_EVENT_SUB_TYPES.VISITOR_JOINED,
      };
      this.createGeneralCallEvent(visitorJoinedCallDto);
    },
    setVisitorLeftCall() {
      const visitorLeftCallDto = {
        visit_id: this.visitId,
        type: CALL_EVENT_TYPES.EVENT,
        sub_type: CALL_EVENT_EVENT_SUB_TYPES.VISITOR_LEFT,
      };
      this.createGeneralCallEvent(visitorLeftCallDto);
    },
    async createGeneralCallEvent(dto) {
      try {
        await CallEventsService.createGeneralEvent(dto)
      } catch (err) {
        logger.error('Cannot create call event', err, {
          dto,
          visitId: this.visitId,
        }, 'VisitCall:createVisitorEvent');
      }
    },
    /**
     * Show a message to the user, log the error and then redirect back out of the call.
     * @param error
     * @param message
     */
    connectionError(error, message) {
      logger.error(
        'Unable to join room', error, { visitId: this.visitId }, 'VisitCall:joinRoom:2',
      );
      if (this.callState === CALL_STATES.PAUSED_MT) {
        this.$swal({
          title: 'Unable to enter call while it is paused by establishment',
          text: 'Please try again shortly.',
          icon: 'warning',
          button: 'Ok',
        }).then(() => {
          // Redirect to visit details
          this.goToWaitingRoom();
        });
      } else {
        this.$swal({
          title: 'Unable to join call',
          text: message,
          icon: 'warning',
          button: 'Ok',
        }).then(() => {
          // Redirect to visit details
          this.goToWaitingRoom();
        });
      }
    },
    /**
     * If the session Terminates or the rooms is destroyed exit out.
     */
    async sessionFinished() {
      // We're leaving
      this.setVisitorLeftCall();

      if (this.leavingNaturally !== true) {
        // Show popup to say the room was terminated
        this.$toast.warning('The room has been terminated, you will be redirected back to the dashboard', {
          toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
        });
      }

      // Redirect
      this.navigateAway();
    },
    getVisitEndTime() {
      if (this.visit && this.visit.booked_slot) {
        const endDateTime = getDateTimeOfSlot(this.visit.booked_slot).endDate;
        return moment(endDateTime).format('HH:mm');
      }
      return '-';
    },
    // Messages
    getWaitingForText() {
      const contact = this.loadingVisit ? 'contact' : this.visit.prisoner.firstname;
      return `'Waiting for' ${contact}`;
    },
    getPrisonerNoVideoText() {
      const contact = this.loadingVisit ? 'contact' : this.visit.prisoner.firstname;
      return `${contact} is having connection issues. Waiting for their connection to improve.`;
    },
    getMtPausedText() {
      return 'An establishment officer has joined the call and paused the stream.';
    },
    getPausedAppInBackgroundText() {
      return 'The call has been paused due to losing focus on the app. The call will resume once they return to the app';
    },
    // Routing
    navigateAway() {
      if (this.isIntakeUser) {
        // Check if call has run out of time
        if (this.leavingDueToCallCountdown) {
          this.$router.push({ name: 'call-ended' });
        } else {
          this.goToWaitingRoom();
        }
      } else {
        this.$router.push({ name: 'dashboard' });
      }
    },
    goToWaitingRoom() {
      // eslint-disable-next-line no-underscore-dangle
      this.$router.push({ name: 'waiting-room', params: { id: this.visit._id } });
    },
    // Screen Shots
    checkForScreenshots() {
      document.addEventListener('keyup', code => {
        if (code.key === 'PrintScreen') {
          this.createGeneralCallEvent({
            visit_id: this.visitId,
            type: CALL_EVENT_TYPES.EVENT,
            sub_type: CALL_EVENT_EVENT_SUB_TYPES.VISITOR_SCREEN_SHOT,
          });
        }
      });
    },
  },
}
</script>
